/**
 * SSEEmitter —— NF 跨域 SSE 统一封装 (fetch-based, 非 EventSource)。
 *
 * 为何不用 EventSource: 不支持 POST、不支持自定义 header、跨域 cookie 受限。
 * NF 两条 SSE 通道都用它:
 *   - POST /v1/chapters/generate/stream  (event: chunk|done|error)
 *   - GET  /v1/chapters/jobs/{id}/stream (event: heartbeat|status|done|error)
 *
 * 提供 spec 风险#2 要求的全部能力: AbortController 卸载即停 · 指数退避重连 ·
 * 重连回调 (onReconnect) 让调用方 GET 拉最新做 reconciliation。
 *
 * 注意: NF 精确 wire 格式 (event 名 / data 结构) 在 M1 对真实端点验证;
 * 此处用通用 (event,data) 解析, 不臆测 payload schema。
 */
import { API_BASE, notifyProjectBudgetGate } from './http'

export interface SSEHandlers {
  /** 每个 SSE 帧: event 名 (无 event: 行时为 'message') + 已 JSON.parse 的 data (失败则原始字符串) */
  onEvent: (event: string, data: unknown) => void
  onOpen?: () => void
  onError?: (err: Error) => void
  /** 断线重连前回调: 调用方应 GET 拉最新状态做 reconciliation (不信缓存) */
  onReconnect?: (attempt: number) => void
}

export interface SSEOptions {
  headers?: Record<string, string>
  method?: 'GET' | 'POST'
  body?: unknown
  /** 最大连续重连次数 (默认 8); 超过则 onError 终止 */
  maxRetries?: number
  baseDelayMs?: number
  maxDelayMs?: number
  /** 收到这些 event 后, EOF 视为服务端正常结束 */
  terminalEvents?: string[]
}

export class SSEConnection {
  private ctrl: AbortController | null = null
  private closed = true
  private attempt = 0
  private timer: ReturnType<typeof setTimeout> | null = null
  private generation = 0
  private sawTerminal = false
  private lastEventId: string | null = null

  constructor(
    private path: string,
    private handlers: SSEHandlers,
    private opts: SSEOptions = {},
  ) {}

  start(): void {
    if (!this.closed || this.ctrl || this.timer) this.close()
    this.closed = false
    void this.run()
  }

  /** 组件卸载调用: 中止流 + 停止重连 */
  close(): void {
    this.closed = true
    this.generation += 1
    if (this.timer) clearTimeout(this.timer)
    this.timer = null
    this.ctrl?.abort()
    this.ctrl = null
  }

  private async run(): Promise<void> {
    const { method = 'GET', body } = this.opts
    const gen = ++this.generation
    this.sawTerminal = false
    this.ctrl = new AbortController()
    let reader: ReadableStreamDefaultReader<Uint8Array> | null = null
    try {
      const headers: Record<string, string> = { ...this.opts.headers }
      if (body !== undefined) headers['Content-Type'] = 'application/json'
      if (this.lastEventId) headers['Last-Event-ID'] = this.lastEventId
      const res = await fetch(`${API_BASE}${this.path}`, {
        method,
        headers: Object.keys(headers).length > 0 ? headers : undefined,
        credentials: 'include',
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal: this.ctrl.signal,
      })
      if (gen !== this.generation) return
      if (!res.ok) throw new Error(`SSE HTTP ${res.status}`)
      if (!res.body) throw new Error('SSE 无响应流')

      this.handlers.onOpen?.()
      this.attempt = 0 // 成功连上 → 重置连续失败计数

      reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      for (;;) {
        const { done, value } = await reader.read()
        if (gen !== this.generation) return
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const frames = buffer.split(/\r?\n\r?\n/)
        buffer = frames.pop() ?? ''
        for (const frame of frames) {
          if (gen !== this.generation) return
          this.emitFrame(frame, gen)
        }
      }

      buffer += decoder.decode()
      if (buffer.trim()) {
        if (gen !== this.generation) return
        this.emitFrame(buffer, gen)
      }

      if (gen !== this.generation || this.closed) return
      if (this.sawTerminal) {
        this.closed = true
      } else {
        void this.scheduleReconnect(new Error('SSE stream ended before terminal event'))
      }
    } catch (err) {
      if (gen !== this.generation || this.closed) return // 主动 abort 或已被新连接取代, 静默
      const e = err instanceof Error ? err : new Error(String(err))
      if (e.name === 'AbortError') return
      void this.scheduleReconnect(e)
    } finally {
      if (reader) {
        try {
          reader.releaseLock()
        } catch {
          // best-effort
        }
      }
      if (gen === this.generation) this.ctrl = null
    }
  }

  private emitFrame(frame: string, gen: number): void {
    if (!frame.trim()) return
    let event = 'message'
    const dataLines: string[] = []
    for (const line of frame.split(/\r?\n/)) {
      if (line.startsWith(':')) continue // 注释/心跳行
      if (line.startsWith('event:')) event = line.slice(6).trim()
      else if (line.startsWith('id:')) this.lastEventId = line.slice(3).trim()
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^ /, ''))
    }
    if (dataLines.length === 0 && event === 'message') return
    const raw = dataLines.join('\n')
    let data: unknown = raw
    if (raw) {
      try {
        data = JSON.parse(raw)
      } catch {
        data = raw
      }
    }
    if ((this.opts.terminalEvents ?? ['done', 'error']).includes(event)) {
      this.sawTerminal = true
    }
    if (gen !== this.generation) return
    if (event === 'done' || event === 'meta') notifyProjectBudgetGate(data)
    this.handlers.onEvent(event, data)
  }

  private async scheduleReconnect(err: Error): Promise<void> {
    const max = this.opts.maxRetries ?? 8
    if (this.attempt >= max) {
      this.handlers.onError?.(new Error(`SSE 重连超限 (${max}): ${err.message}`))
      return
    }
    this.attempt += 1
    const base = this.opts.baseDelayMs ?? 500
    const cap = this.opts.maxDelayMs ?? 15000
    // 指数退避 + 抖动
    const delay = Math.min(cap, base * 2 ** (this.attempt - 1)) * (0.7 + Math.random() * 0.6)
    this.handlers.onReconnect?.(this.attempt)
    if (this.timer) clearTimeout(this.timer)
    this.timer = setTimeout(() => {
      this.timer = null
      if (!this.closed) void this.run()
    }, delay)
  }
}

/** 便捷: 起一条 SSE 连接, 返回 close 句柄 (配合 useEffect 卸载) */
export function openSSE(path: string, handlers: SSEHandlers, opts?: SSEOptions): () => void {
  const conn = new SSEConnection(path, handlers, opts)
  conn.start()
  return () => conn.close()
}
